import { addDays, addMinutes, isAfter, isBefore, startOfDay } from 'date-fns';
import { OperationNames } from 'events.schema';

import { OrderEntity } from '@/common/types/entities';
import { RemoteProjectChangeEvent } from '@/features/realTimeCollaboration/types';

export const checkIfOrderTasksChanged = (event: RemoteProjectChangeEvent, orderId: string) => {
  return (
    event.operation.name === OperationNames.UpdateOrders &&
    event.operation.input.some((input) => input.id === orderId && input.tasks !== undefined)
  );
};

/**
 * Returns the actual end date of an order that should be displayed in the schedule. Considers finishedAt, if set.
 * If includeDryingBreak is true, the drying break is included in the calculation.
 * @param order
 * @param includeDryingBreak
 * @returns
 */
export function getDisplayedOrderEndDate(
  order: OrderEntity,
  { includeDryingBreak = false, isHourly }: { includeDryingBreak?: boolean; isHourly: boolean },
) {
  const getCorrectFinishedAt = (finishedAt: Date) => {
    // On daily projects, we align finishedAt to the start of the next day
    if (!isHourly) {
      return startOfDay(addDays(finishedAt, 1));
    }
    return finishedAt;
  };

  // If actual finished date is smaller than (before the) planned finish date,
  // And the finishedDate is AFTER the startDate
  // use this as displayed date
  const finishedDate =
    order.finishedAt &&
    isAfter(order.finishedAt, order.startAt) &&
    isBefore(order.finishedAt, order.finishAt)
      ? getCorrectFinishedAt(order.finishedAt)
      : null;
  const endDate = finishedDate ?? order.finishAt;

  if (includeDryingBreak) {
    const dryingBreakDuration = order.dryingBreak?.duration ?? 0;
    return new SchedulingDate(addMinutes(endDate, dryingBreakDuration));
  }
  return new SchedulingDate(endDate);
}
